import { createAsyncThunk } from '@reduxjs/toolkit';
import { entriesQueryStringBuilder } from 'modules/inbound/entries/application/EntriesQueryStringBuilder';
import DownloadEntriesUseCase from 'modules/inbound/entries/application/NextHours/DownloadEntriesUseCase';
import getEntriesDataUseCase from 'modules/inbound/entries/application/NextHours/GetEntriesDataUseCase';
import { getEntriesSummaryUseCase } from 'modules/inbound/entries/application/NextHours/GetEntriesSummaryUseCase';
import { getUrgentIncomingTrucksUseCase } from 'modules/inbound/entries/application/NextHours/GetUrgentIncomingTrucksUseCase';
import { getLastUpdateUseCase } from 'modules/inbound/shared/application/LastUpdateUseCases/GetLastUpdateUseCase';
import { LastUpdateType } from 'modules/inbound/shared/domain/LastUpdate/types';
import { NameValues } from 'modules/inbound/shared/domain/NameValues';
import {
  ColumnSelected,
  Sort
} from 'modules/shared/layout/components/TableHeader/types';
import {
  getFilterByCriticalPieces,
  getFilterByUrgentTrucks,
  getFilterSearch,
  getPageInfo,
  getSelectedColumn,
  getSelectedEntry,
  getSelectedFilters
} from '.';
import { getSelectedHour } from '../DischargeDocksReducer';
import { SelectedHour } from '../DischargeDocksReducer/types';
import { SelectedSubHeaderFilters } from './types';

const getFiltersQuery = (state) => {
  const searchValue: string = getFilterSearch(state);
  const filterByUrgentTrucks: boolean = getFilterByUrgentTrucks(state);
  const filterByCriticalPiece: boolean = getFilterByCriticalPieces(state);
  const { utcDate }: SelectedHour = getSelectedHour(state);
  const {
    selectedWarehouses,
    selectedSuppliers,
    selectedUnloadingPoints
  }: SelectedSubHeaderFilters = getSelectedFilters(state);

  const filters = {
    [NameValues.SUPPLIER_NAME]: selectedSuppliers.map(
      (supplier) => supplier.key
    ),
    [NameValues.WAREHOUSE]: selectedWarehouses.map((supplier) => supplier.key),
    [NameValues.UNLOADING_POINT]: selectedUnloadingPoints.map(
      (unloadingPoint) => unloadingPoint.key
    ),
    [NameValues.CODE]: searchValue,
    [NameValues.URGENT]: filterByUrgentTrucks,
    [NameValues.HAS_CRITICAL_PIECES]: filterByCriticalPiece,
    [NameValues.DATE]: utcDate
  };

  return entriesQueryStringBuilder(filters);
};

const getEntriesNextHoursLastUpdateData = createAsyncThunk(
  'entriesST12/getEntriesNextHoursLastUpdateData',
  () =>
    getLastUpdateUseCase.execute({
      queryParams: { key: LastUpdateType.entriesIncoming }
    })
);
const getEntriesST12Data = createAsyncThunk(
  'entriesST12/getEntriesData',
  async (
    params:
      | {
          workshop_query?: boolean | string;
          unloading_point?: string;
          booked?: number;
          start_time?: string;
          end_time?: string;
          search?: string;
          docks?: string[];
        }
      | undefined,
    { getState }
  ) => {
    const appState = getState();
    const { page, size } = getPageInfo(appState);
    const selectedColumn: ColumnSelected = getSelectedColumn(appState);

    const sortOrder = selectedColumn.isAscending ? Sort.ASC : Sort.DESC;
    const sort = `{${selectedColumn.key}:${sortOrder}}`;

    const queryString = getFiltersQuery(appState);

    const queryParams = {
      page: params?.docks?.length ? 1 : page,
      size: params?.docks?.length ? 50 : size,
      sort: selectedColumn.key.length ? sort : '',
      search: params?.search ?? (!!queryString.length && `{${queryString}}`),
      workshop_query:
        params?.workshop_query === true
          ? 'true'
          : params?.workshop_query ?? false,
      ...(params?.unloading_point
        ? { unloading_point: params.unloading_point }
        : {}),
      ...(typeof params?.booked === 'number' ? { booked: params.booked } : {}),
      ...(params?.start_time ? { start_time: params.start_time } : {}),
      ...(params?.end_time ? { end_time: params.end_time } : {})
    };

    if (params?.docks?.length) {
      const responses = await Promise.all(
        params.docks.map((dock) =>
          getEntriesDataUseCase.execute({
            queryParams: {
              ...queryParams,
              dock
            }
          })
        )
      );

      const totalCount = responses.reduce(
        (total, response) => total + response.pagination.totalCount,
        0
      );

      return {
        entries: responses.flatMap((response) => response.entries),
        pagination: {
          ...responses[0].pagination,
          page: 1,
          size: 50,
          pageCount: Math.ceil(totalCount / 50),
          totalCount
        }
      };
    }

    const response = await getEntriesDataUseCase.execute({ queryParams });
    return response;
  }
);

const getEntryST12DetailData = createAsyncThunk(
  'entriesST12/getEntryDetailData',
  async (_, { getState }) => {
    const appState = getState();
    const { transportId } = getSelectedEntry(appState);

    const response = await getEntriesDataUseCase.getDetail({
      transportId: transportId
    });

    return response;
  }
);

const getUrgentIncomingTrucks = createAsyncThunk(
  'entriesST12/getUrgentIncomingTrucks',
  async () => getUrgentIncomingTrucksUseCase.execute()
);

const getEntriesST12SummaryData = createAsyncThunk(
  'entriesST12/getEntriesSummaryData',
  async (_, { getState }) => {
    const appState = getState();

    const queryString = getFiltersQuery(appState);

    const queryParams = {
      search: !!queryString.length && `{${queryString}}`
    };

    const response = await getEntriesSummaryUseCase.execute({
      queryParams
    });
    return response;
  }
);

const getEntriesST12NextHoursLastUpdateData = createAsyncThunk(
  'entriesST12/getEntriesNextHoursLastUpdateData',
  () =>
    getLastUpdateUseCase.execute({
      queryParams: { key: LastUpdateType.entriesIncoming }
    })
);

const downloadEntriesST12 = createAsyncThunk(
  'entriesST12/downloadEntries',
  async (_, { getState }) => {
    const appState = getState();
    const queryString = getFiltersQuery(appState);

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const queryParams = {
      tz: timeZone,
      search: queryString && `{${queryString}}`
    };
    const response = await DownloadEntriesUseCase.execute({
      queryParams
    });
    return response;
  }
);

export {
  downloadEntriesST12,
  getEntriesNextHoursLastUpdateData,
  getEntriesST12Data,
  getEntriesST12NextHoursLastUpdateData,
  getEntriesST12SummaryData,
  getEntryST12DetailData,
  getUrgentIncomingTrucks
};
